import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { Modal, Button, Select, Radio, Spin, Empty, Tooltip, Space, DatePicker, Tabs, Card, Statistic, Skeleton, Input } from 'antd';
import { 
  LineChart, Line, 
  BarChart, Bar, 
  AreaChart, Area, 
  PieChart, Pie, Cell,
  XAxis, YAxis, CartesianGrid, 
  Tooltip as RechartsTooltip, 
  Legend, ResponsiveContainer 
} from 'recharts';
import { 
  InfoCircleOutlined, 
  DownloadOutlined, 
  ShoppingCartOutlined, 
  UserOutlined, 
  DollarOutlined, 
  TagOutlined,
  ClockCircleOutlined,
  LineChartOutlined,
  BarChartOutlined,
  AreaChartOutlined,
  AppstoreOutlined
} from '@ant-design/icons';
import dayjs from 'dayjs';
import isSameOrAfter from 'dayjs/plugin/isSameOrAfter';
import isSameOrBefore from 'dayjs/plugin/isSameOrBefore';
import api from '../../../services/api';
import { formatMetricValue, getCurrencySymbol } from '../utils/formatters';

dayjs.extend(isSameOrAfter);
dayjs.extend(isSameOrBefore);

const { Option } = Select;
const { RangePicker } = DatePicker;

const MetricChartModal = ({ 
  visible, 
  onCancel, 
  brandId, 
  brandName,
  metrics,
  allMetricsData,
  initialTab = 'chart',
  allCampaigns = []
}) => {
  const [dataType, setDataType] = useState('daily'); // 'daily' or 'monthly'
  const [chartMode, setChartMode] = useState('compare'); // 'compare' default olacak
  const [chartType, setChartType] = useState('line'); // 'line', 'bar', 'area'
  const [selectedMetrics, setSelectedMetrics] = useState([]);
  const [loading, setLoading] = useState(false);
  const [chartData, setChartData] = useState([]);
  const [normalizedChartData, setNormalizedChartData] = useState([]);
  const [availableMetrics, setAvailableMetrics] = useState([]);
  const [brandCurrency, setBrandCurrency] = useState('TRY');
  const [metricNames, setMetricNames] = useState({});
  const [dateRange, setDateRange] = useState([
    dayjs().subtract(7, 'day'), // Bugün hariç son 1 hafta
    dayjs().subtract(1, 'day')  // Dün
  ]);
  const [monthlyDataCache, setMonthlyDataCache] = useState({});
  const [dailyDataCache, setDailyDataCache] = useState({});
  const [customQuestion, setCustomQuestion] = useState("");
  const [isAskingCustomQuestion, setIsAskingCustomQuestion] = useState(false);
  const [activeTab, setActiveTab] = useState(initialTab);
  const [aiDateRange, setAiDateRange] = useState([
    dayjs().subtract(7, 'day'), // AI için ayrı tarih aralığı
    dayjs().subtract(1, 'day')
  ]);
  const [preservedMetrics, setPreservedMetrics] = useState([]); // Preserve metrics when switching data types
  const [shouldPreserveMetrics, setShouldPreserveMetrics] = useState(false); // Flag to track if we should preserve

  const COLORS = [
    '#1f77b4', // mavi
    '#ff7f0e', // turuncu
    '#2ca02c', // yeşil
    '#d62728', // kırmızı
    '#9467bd', // mor
    '#8c564b', // kahverengi
    '#e377c2', // pembe
    '#7f7f7f', // gri
    '#bcbd22', // sarımsı-yeşil
    '#17becf'  // açık mavi
  ];

  const getLineColor = (index) => {
    return COLORS[index % COLORS.length];
  };

  // Tarih aralığındaki kampanyaları filtrele
  const getActiveCampaigns = (startDate, endDate) => {
    if (!allCampaigns || allCampaigns.length === 0) return [];
    
    // Bu markaya ait kampanyaları filtrele
    const brandCampaigns = allCampaigns.filter(campaign => 
      campaign.Brand && campaign.Brand.id === brandId
    );
    
    // Tarih aralığında aktif olanları filtrele
    const activeCampaigns = brandCampaigns.filter(campaign => {
      const campaignStart = dayjs(campaign.startDate);
      const campaignEnd = dayjs(campaign.endDate);
      
      // Kampanya ve analiz aralığı örtüşme kontrolü
      const overlaps = (
        // Kampanya başlangıcı analiz aralığında
        (campaignStart.isSameOrAfter(startDate) && campaignStart.isSameOrBefore(endDate)) ||
        // Kampanya bitişi analiz aralığında  
        (campaignEnd.isSameOrAfter(startDate) && campaignEnd.isSameOrBefore(endDate)) ||
        // Kampanya analiz aralığını kapsıyor
        (campaignStart.isSameOrBefore(startDate) && campaignEnd.isSameOrAfter(endDate))
      );
      
      return overlaps;
    });
    
    return activeCampaigns.map(campaign => ({
      name: campaign.name,
      startDate: dayjs(campaign.startDate).format('DD.MM.YYYY'),
      endDate: dayjs(campaign.endDate).format('DD.MM.YYYY'),
      description: campaign.description || ''
    }));
  };

  // Marka bilgisini kontrol et
  useEffect(() => {
    if (brandId && allMetricsData && allMetricsData[brandId]) {
      const currency = allMetricsData[brandId]?.brandInfo?.currency || 'TRY';
      setBrandCurrency(currency);
    }
  }, [brandId, allMetricsData]);

  // Günlük metrikler için ayrı useEffect - dateRange'e bağlı değil
  useEffect(() => {
    if (!brandId || !visible || dataType !== 'daily') return;
    
    const loadDailyMetrics = () => {
      setLoading(true);
      try {
        if (metrics && metrics[brandId]) {
          const sortedMetrics = [...metrics[brandId]]
            .filter(metric => metric.BrandMetric?.isVisible !== false)
            .sort((a, b) => {
              const orderA = a.BrandMetric?.displayOrder || 0;
              const orderB = b.BrandMetric?.displayOrder || 0;
              return orderA - orderB;
            });
          setAvailableMetrics(sortedMetrics);
        }
      } finally {
        setLoading(false);
      }
    };

    loadDailyMetrics();
  }, [brandId, visible, dataType, metrics]);

  // Modal açıldığında initialTab'ı activeTab'a uygula
  useEffect(() => {
    if (visible) {
      setActiveTab(initialTab);
    }
  }, [visible, initialTab]);

  // Metrics preservation logic - hem daily hem monthly için ortak
  useEffect(() => {
    if (!availableMetrics.length) return;
    
    if (activeTab !== 'askAI') {
      if (shouldPreserveMetrics && preservedMetrics.length > 0) {
        // Preserve edilen metriklerin hala mevcut olup olmadığını kontrol et
        const validPreservedMetrics = preservedMetrics.filter(id => 
          availableMetrics.some(metric => metric.id === id)
        );
        if (validPreservedMetrics.length > 0) {
          setSelectedMetrics(validPreservedMetrics);
        } else {
          // Preserve edilen metrikler mevcut değilse default'a dön (revenue veya ilk metrik)
          const revenueMetric = availableMetrics.find(metric => metric.id === 1);
          const firstMetric = availableMetrics[0];
          if (revenueMetric) {
            setSelectedMetrics([1]);
          } else if (firstMetric) {
            setSelectedMetrics([firstMetric.id]);
          }
        }
        setPreservedMetrics([]);
        setShouldPreserveMetrics(false);
      } else if (selectedMetrics.length === 0) {
        // Sadece hiç metrik seçili değilse default'a dön (revenue veya ilk metrik)
        const revenueMetric = availableMetrics.find(metric => metric.id === 1);
        const firstMetric = availableMetrics[0];
        if (revenueMetric) {
          setSelectedMetrics([1]);
        } else if (firstMetric) {
          setSelectedMetrics([firstMetric.id]);
        }
      }
    }
  }, [activeTab, preservedMetrics, shouldPreserveMetrics, availableMetrics]);

  // Aylık metrikler için ayrı useEffect - dateRange'e bağlı
  useEffect(() => {
    if (!brandId || !visible || dataType !== 'monthly') return;
    
    const loadMonthlyMetrics = async () => {
      setLoading(true);
      try {
        // Aylık metrikler - Tarih aralığındaki tüm aylar için veriyi çek
        const startDate = dateRange[0].format('YYYY-MM');
        const endDate = dateRange[1].format('YYYY-MM');
        
        // Tarih aralığındaki ayları belirle
        const monthsData = [];
        const start = dayjs(dateRange[0]);
        const end = dayjs(dateRange[1]);
        let current = start.clone().startOf('month');
        
        while (current.isBefore(end) || current.isSame(end, 'month')) {
          const monthStr = current.format('YYYY-MM');
          monthsData.push(monthStr);
          current = current.add(1, 'month');
        }

        // Henüz çekilmemiş aylar için API çağrısı yap
        const monthsToFetch = monthsData.filter(month => !monthlyDataCache[month]);
        
        if (monthsToFetch.length > 0) {
          const newMonthlyData = await Promise.all(
            monthsToFetch.map(async (month) => {
              try {
                const response = await api.get(`/monthly-metrics/brands/${brandId}`, {
                  params: { date: month }
                });
                return { month, data: response.data || [] };
              } catch (error) {
                console.error(`${month} verisi alınırken hata:`, error);
                return { month, data: [] };
              }
            })
          );

          // Cache'i güncelle
          const updatedCache = { ...monthlyDataCache };
          newMonthlyData.forEach(({ month, data }) => {
            updatedCache[month] = data;
          });
          setMonthlyDataCache(updatedCache);
        }

        // Tüm ayların verilerini birleştir ve metrikleri belirle
        const allMetricsArray = [];
        monthsData.forEach(month => {
          const monthData = monthlyDataCache[month] || [];
          monthData.forEach(metric => {
            if (metric.isVisible !== false) {
              // Aynı metriği birden fazla eklemeyi önle
              if (!allMetricsArray.some(m => m.id === metric.id)) {
                allMetricsArray.push(metric);
              }
            }
          });
        });
        
        setAvailableMetrics(allMetricsArray);
      } catch (error) {
        console.error('Aylık metrikler yüklenirken bir hata oluştu:', error);
      } finally {
        setLoading(false);
      }
    };

    loadMonthlyMetrics();
  }, [brandId, visible, dataType, dateRange, monthlyDataCache]);


  // Grafik verilerini yükle
  useEffect(() => {
    if (!brandId || !visible || selectedMetrics.length === 0) {
      setChartData([]);
      setNormalizedChartData([]);
      return;
    }

    const fetchChartData = async () => {
      setLoading(true);
      try {
        // Önce metrik adlarını belirle
        const newMetricNames = {};
        selectedMetrics.forEach(metricId => {
          // Metrik ID string olabilir, number'a çevirelim
          const numericMetricId = parseInt(metricId, 10);
          const metric = availableMetrics.find(m => m.id === numericMetricId);
          if (metric) {
            newMetricNames[metricId] = metric.description || metric.name;
          } else {
            newMetricNames[metricId] = `Metrik ${metricId}`;
          }
        });
        setMetricNames(newMetricNames);

        let rawData = [];

        if (dataType === 'daily') {
          const startDate = dateRange[0].format('YYYY-MM-DD');
          const endDate = dateRange[1].format('YYYY-MM-DD');
          const cacheKey = `${startDate}_${endDate}`;

          // Cache'de veri yoksa API'den çek
          if (!dailyDataCache[cacheKey]) {
            const response = await api.get('/data', {
              params: {
                brandId,
                startDate,
                endDate
              }
            });
            setDailyDataCache(prev => ({ ...prev, [cacheKey]: response.data }));
          }

          const responseData = dailyDataCache[cacheKey] || [];
          
          // Grafik için veriyi düzenle
          const dailyData = [];
          const start = dayjs(startDate);
          const end = dayjs(endDate);
          const diffDays = end.diff(start, 'day') + 1;
          
          for (let i = 0; i < diffDays; i++) {
            const currentDate = start.add(i, 'day');
            const formattedDate = currentDate.format('YYYY-MM-DD');
            const displayDate = currentDate.format('DD.MM.YYYY');
            
            const dayData = { 
              date: formattedDate, 
              displayDate
            };
            
            selectedMetrics.forEach(metricId => {
              const metricData = responseData.find(item => 
                item.Metric?.id === parseInt(metricId, 10) && 
                dayjs(item.date).format('YYYY-MM-DD') === formattedDate
              );
              
              dayData[`metric_${metricId}`] = metricData ? parseFloat(metricData.value) : null;
            });
            
            dailyData.push(dayData);
          }
          
          rawData = dailyData;
        } else {
          // Aylık verileri cache'den al
          const startDate = dateRange[0].format('YYYY-MM');
          const endDate = dateRange[1].format('YYYY-MM');
          
          const monthsData = [];
          const start = dayjs(dateRange[0]);
          const end = dayjs(dateRange[1]);
          let current = start.clone().startOf('month');
          
          while (current.isBefore(end) || current.isSame(end, 'month')) {
            const monthStr = current.format('YYYY-MM');
            const displayMonth = current.format('MMM YYYY');
            
            monthsData.push({ 
              date: monthStr, 
              displayDate: displayMonth
            });
            
            current = current.add(1, 'month');
          }
          
          // Cache'den verileri al ve grafik verisini oluştur
          const chartData = [];
          monthsData.forEach(({ date, displayDate }) => {
            const monthData = { date, displayDate };
            const cachedData = monthlyDataCache[date] || [];
            
            selectedMetrics.forEach(metricId => {
              const metricValue = cachedData.find(m => m.id === parseInt(metricId, 10));
              monthData[`metric_${metricId}`] = metricValue ? parseFloat(metricValue.value) : null;
            });
            
            chartData.push(monthData);
          });
          
          rawData = chartData;
        }

        // Set regular chart data
        setChartData(rawData);
        
        // Normalize chart data for comparison mode
        const normalizedData = normalizeChartData(rawData, selectedMetrics);
        setNormalizedChartData(normalizedData);

      } catch (error) {
        console.error('Grafik verileri yüklenirken bir hata oluştu:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchChartData();
  }, [brandId, dataType, selectedMetrics, dateRange, visible, availableMetrics, monthlyDataCache, dailyDataCache]);

  // Normalize chart data for comparison
  const normalizeChartData = (rawData, metricIds) => {
    // Metriklerin min ve max değerlerini bul
    const metricRanges = {};
    const dailyChanges = {};
    
    metricIds.forEach(metricId => {
      const metricKey = `metric_${metricId}`;
      const values = rawData
        .map(item => item[metricKey])
        .filter(value => value !== null && value !== undefined);
      
      if (values.length > 0) {
        metricRanges[metricId] = {
          min: Math.min(...values),
          max: Math.max(...values)
        };
      }
      
      // Günlük değişim yüzdelerini hesapla
      for (let i = 1; i < rawData.length; i++) {
        const currentValue = rawData[i][metricKey];
        const previousValue = rawData[i-1][metricKey];
        
        if (currentValue !== null && currentValue !== undefined && 
            previousValue !== null && previousValue !== undefined && 
            previousValue !== 0) {
          
          if (!dailyChanges[i]) {
            dailyChanges[i] = {};
          }
          
          // Değişim yüzdesi: (yeni - eski) / eski * 100
          dailyChanges[i][metricId] = ((currentValue - previousValue) / Math.abs(previousValue)) * 100;
        }
      }
    });
    
    // Tüm veriyi normalize et
    return rawData.map((item, index) => {
      const normalizedItem = { ...item };
      
      metricIds.forEach(metricId => {
        const metricKey = `metric_${metricId}`;
        const normalizedKey = `normalized_${metricId}`;
        const value = item[metricKey];
        
        if (value !== null && value !== undefined && metricRanges[metricId]) {
          const { min, max } = metricRanges[metricId];
          
          // Min-Max normalizasyonu (0-100 aralığına getir)
          if (min === max) {
            normalizedItem[normalizedKey] = 50; // Min ve max aynıysa ortada göster
          } else {
            normalizedItem[normalizedKey] = ((value - min) / (max - min)) * 100;
          }
          
          // Orijinal değeri de sakla
          normalizedItem[`${normalizedKey}_original`] = value;
          
          // Günlük değişim yüzdesini ekle (varsa)
          if (dailyChanges[index] && dailyChanges[index][metricId] !== undefined) {
            normalizedItem[`${normalizedKey}_change`] = dailyChanges[index][metricId];
          }
        } else {
          normalizedItem[normalizedKey] = null;
          normalizedItem[`${normalizedKey}_original`] = value;
        }
      });
      
      return normalizedItem;
    });
  };

  // Metrik değişikliği
  const handleMetricChange = (selectedIds) => {
    setSelectedMetrics(selectedIds);
  };

  // Filter function'ı useCallback ile optimize et
  const metricFilterOption = useCallback((input, option) => {
    const metric = availableMetrics.find(m => m.id === option.value);
    if (!metric) return false;
    
    const searchText = input.toLowerCase();
    const description = (metric.description || '').toLowerCase();
    const name = (metric.name || '').toLowerCase();
    
    return description.includes(searchText) || name.includes(searchText);
  }, [availableMetrics]);

  // Veri tipi değişikliği 
  const handleDataTypeChange = (e) => {
    // Mevcut seçili metrikleri sakla
    if (selectedMetrics.length > 0) {
      setPreservedMetrics(selectedMetrics);
      setShouldPreserveMetrics(true);
    }
    setDataType(e.target.value);
    // Data type değiştiğinde selectedMetrics'i temizle
    setSelectedMetrics([]);
    // availableMetrics'i de temizle ki eski metrikler görünmesin
    setAvailableMetrics([]);
  };

  // Görünüm modu değişikliği
  const handleChartModeChange = (e) => {
    setChartMode(e.target.value);
  };

  // Grafik türü değişikliği
  const handleChartTypeChange = (e) => {
    setChartType(e.target.value);
  };

  // Metriğin adını getir
  const getMetricName = (metricId) => {
    const metric = availableMetrics.find(m => m.id === parseInt(metricId, 10));
    return metric ? (metric.description || metric.name) : `Metrik ${metricId}`;
  };

  const handleDateRangeChange = (dates) => {
    if (dates && dates.length === 2) {
      // Mevcut seçili metrikleri sakla
      if (selectedMetrics.length > 0) {
        setPreservedMetrics(selectedMetrics);
        setShouldPreserveMetrics(true);
      }
      setDateRange(dates);
    }
  };

  // Grafik verilerini CSV olarak indirme
  const handleDownloadCSV = () => {
    if (!chartData || chartData.length === 0) return;
    
    // CSV başlık satırı oluştur
    const headers = ['Tarih'];
    selectedMetrics.forEach(metricId => {
      const numericMetricId = typeof metricId === 'string' ? parseInt(metricId, 10) : metricId;
      const metric = availableMetrics.find(m => m.id === numericMetricId);
      const metricName = metric ? (metric.name || metric.description) : `Metrik ${numericMetricId}`;
      headers.push(metricName);
    });
    
    // CSV satırlarını oluştur
    const rows = chartData.map(dataPoint => {
      const row = [dataPoint.displayDate];
      selectedMetrics.forEach(metricId => {
        const value = dataPoint[`metric_${metricId}`];
        row.push(value !== null && value !== undefined ? value : '');
      });
      return row;
    });
    
    // CSV formatına dönüştür
    let csvContent = headers.join(',') + '\n';
    rows.forEach(row => {
      csvContent += row.join(',') + '\n';
    });
    
    // CSV dosyasını indir
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.setAttribute('href', url);
    link.setAttribute('download', `${brandName}-metrics-${dayjs().format('YYYY-MM-DD')}.csv`);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  // Handle "Ask Ai" button click
  const handleAskAI = async () => {
    if (selectedMetrics.length === 0) {
      Modal.info({
        title: "Not Enough Data",
        content: "Please ensure you have selected metrics before asking Ai.",
      });
      return;
    }

    // Fetch data for AI date range
    setLoading(true);
    try {
      let aiData = [];
      
      if (dataType === 'daily') {
        const startDate = aiDateRange[0].format('YYYY-MM-DD');
        const endDate = aiDateRange[1].format('YYYY-MM-DD');
        
        const response = await api.get('/data', {
          params: {
            brandId,
            startDate,
            endDate
          }
        });
        
        const responseData = response.data || [];
        
        // Format data for AI
        const start = dayjs(startDate);
        const end = dayjs(endDate);
        const diffDays = end.diff(start, 'day') + 1;
        
        for (let i = 0; i < diffDays; i++) {
          const currentDate = start.add(i, 'day');
          const formattedDate = currentDate.format('YYYY-MM-DD');
          const displayDate = currentDate.format('DD.MM.YYYY');
          
          const dayData = { 
            date: formattedDate, 
            displayDate
          };
          
          selectedMetrics.forEach(metricId => {
            const metricData = responseData.find(item => 
              item.Metric?.id === parseInt(metricId, 10) && 
              dayjs(item.date).format('YYYY-MM-DD') === formattedDate
            );
            
            dayData[`metric_${metricId}`] = metricData ? parseFloat(metricData.value) : null;
          });
          
          aiData.push(dayData);
        }
      } else {
        // Monthly data for AI date range
        const start = dayjs(aiDateRange[0]);
        const end = dayjs(aiDateRange[1]);
        let current = start.clone().startOf('month');
        
        while (current.isBefore(end) || current.isSame(end, 'month')) {
          const monthStr = current.format('YYYY-MM');
          const displayMonth = current.format('MMM YYYY');
          
          // Fetch monthly data if not in cache
          if (!monthlyDataCache[monthStr]) {
            const response = await api.get(`/monthly-metrics/brands/${brandId}`, {
              params: { date: monthStr }
            });
            setMonthlyDataCache(prev => ({ ...prev, [monthStr]: response.data || [] }));
          }
          
          const monthData = { 
            date: monthStr, 
            displayDate: displayMonth
          };
          
          const cachedData = monthlyDataCache[monthStr] || [];
          selectedMetrics.forEach(metricId => {
            const metricValue = cachedData.find(m => m.id === parseInt(metricId, 10));
            monthData[`metric_${metricId}`] = metricValue ? parseFloat(metricValue.value) : null;
          });
          
          aiData.push(monthData);
          current = current.add(1, 'month');
        }
      }

      // Aktif kampanyaları al
      const activeCampaigns = getActiveCampaigns(aiDateRange[0], aiDateRange[1]);
      
      // Prepare data for Gemini API
      const dataForAI = {
        brandId,
        brandName,
        startDate: aiDateRange[0].format(dataType === 'daily' ? 'DD.MM.YYYY' : 'MM.YYYY'),
        endDate: aiDateRange[1].format(dataType === 'daily' ? 'DD.MM.YYYY' : 'MM.YYYY'),
        dataType: dataType,
        metrics: selectedMetrics.map(metricId => ({
          id: metricId,
          name: getMetricName(metricId),
        })),
        seriesData: aiData.map(dataPoint => {
          const point = { date: dataPoint.displayDate };
          selectedMetrics.forEach(metricId => {
            point[`metric_${metricId}`] = dataPoint[`metric_${metricId}`];
          });
          return point;
        }),
        campaigns: activeCampaigns
      };

      if (aiData.length === 0) {
        Modal.warning({
          title: "No Series Data for AI",
          content: `No time series data available for the selected range to send to AI.`,
        });
        return;
      }

      console.log("Data to send to AI (for date range analysis):", dataForAI); // For debugging

      const response = await api.post('/gemini/ask', dataForAI, { timeout: 600000 }); 
      
      // Basic formatter, assuming backend might send simple HTML or markdown-like lists
      const formatAIResponseForDisplay = (text) => {
        if (!text) return "<p>No response from AI.</p>";
        let htmlText = text;
        htmlText = htmlText.replace(/\*\*\s*(.*?)\s*\*\*/g, '<strong>$1</strong>');
        htmlText = htmlText.replace(/\*\s*(.*?)\s*\*/g, '<em>$1</em>');
        if (!htmlText.match(/<p>|<br>/i)) {
            htmlText = htmlText.replace(/\n/g, '<br />');
        }
        htmlText = htmlText.replace(/^-{1,}\s*/gm, '<br />&bull; ');
        htmlText = htmlText.replace(/^\*\s+/gm, '<br />&bull; ');
        htmlText = htmlText.replace(/^\d+\.\s+/gm, (match) => `<br />  ${match}`);
        return htmlText;
      };

      Modal.success({
        title: "Ask Ai Insight", // Updated Bot Name
        content: (
          <div style={{ maxHeight: '60vh', overflowY: 'auto', padding: '10px', border: '1px solid #f0f0f0', borderRadius: '4px' }}>
            {/* Use dangerouslySetInnerHTML to render HTML tags from AI response */}
            <div dangerouslySetInnerHTML={{ __html: formatAIResponseForDisplay(response.data.insight) }} />
          </div>
        ),
        width: 700,
        maskClosable: true,
      });

    } catch (error) {
      console.error("Error asking AI:", error);
      Modal.error({
        title: "AI Error",
        content: "Could not get insights from AI. Please try again later. Details: " + (error.response?.data?.error || error.message || "Unknown error"),
      });
    } finally {
      setLoading(false);
    }
  };

  // Handle Custom Question to AI
  const handleAskCustomQuestion = async () => {
    if (!customQuestion.trim()) {
      Modal.info({
        title: "No Question Provided",
        content: "Please type your question in the text area before asking Ask Ai.",
      });
      return;
    }
    if (selectedMetrics.length === 0) {
      Modal.warning({
        title: "Not Enough Context",
        content: "Please select metrics to provide context for your question.",
      });
      return;
    }

    setIsAskingCustomQuestion(true);
    
    try {
      // Fetch data for AI date range
      let aiData = [];
      
      if (dataType === 'daily') {
        const startDate = aiDateRange[0].format('YYYY-MM-DD');
        const endDate = aiDateRange[1].format('YYYY-MM-DD');
        
        const response = await api.get('/data', {
          params: {
            brandId,
            startDate,
            endDate
          }
        });
        
        const responseData = response.data || [];
        
        // Format data for AI
        const start = dayjs(startDate);
        const end = dayjs(endDate);
        const diffDays = end.diff(start, 'day') + 1;
        
        for (let i = 0; i < diffDays; i++) {
          const currentDate = start.add(i, 'day');
          const formattedDate = currentDate.format('YYYY-MM-DD');
          const displayDate = currentDate.format('DD.MM.YYYY');
          
          const dayData = { 
            date: formattedDate, 
            displayDate
          };
          
          selectedMetrics.forEach(metricId => {
            const metricData = responseData.find(item => 
              item.Metric?.id === parseInt(metricId, 10) && 
              dayjs(item.date).format('YYYY-MM-DD') === formattedDate
            );
            
            dayData[`metric_${metricId}`] = metricData ? parseFloat(metricData.value) : null;
          });
          
          aiData.push(dayData);
        }
      } else {
        // Monthly data for AI date range
        const start = dayjs(aiDateRange[0]);
        const end = dayjs(aiDateRange[1]);
        let current = start.clone().startOf('month');
        
        while (current.isBefore(end) || current.isSame(end, 'month')) {
          const monthStr = current.format('YYYY-MM');
          const displayMonth = current.format('MMM YYYY');
          
          // Fetch monthly data if not in cache
          if (!monthlyDataCache[monthStr]) {
            const response = await api.get(`/monthly-metrics/brands/${brandId}`, {
              params: { date: monthStr }
            });
            setMonthlyDataCache(prev => ({ ...prev, [monthStr]: response.data || [] }));
          }
          
          const monthData = { 
            date: monthStr, 
            displayDate: displayMonth
          };
          
          const cachedData = monthlyDataCache[monthStr] || [];
          selectedMetrics.forEach(metricId => {
            const metricValue = cachedData.find(m => m.id === parseInt(metricId, 10));
            monthData[`metric_${metricId}`] = metricValue ? parseFloat(metricValue.value) : null;
          });
          
          aiData.push(monthData);
          current = current.add(1, 'month');
        }
      }
      
      if (aiData.length === 0) {
        Modal.warning({
          title: "No Series Data for AI",
          content: `No time series data available for the selected range.`,
        });
        setIsAskingCustomQuestion(false);
        return;
      }

      // Aktif kampanyaları al
      const activeCampaigns = getActiveCampaigns(aiDateRange[0], aiDateRange[1]);
      
      const dataForCustomAI = {
        brandId,
        brandName,
        startDate: aiDateRange[0].format(dataType === 'daily' ? 'DD.MM.YYYY' : 'MM.YYYY'),
        endDate: aiDateRange[1].format(dataType === 'daily' ? 'DD.MM.YYYY' : 'MM.YYYY'),
        dataType: dataType,
        metrics: selectedMetrics.map(metricId => ({
          id: metricId,
          name: getMetricName(metricId),
        })),
        seriesData: aiData.map(dataPoint => {
          const point = { date: dataPoint.displayDate };
          selectedMetrics.forEach(metricId => {
            point[`metric_${metricId}`] = dataPoint[`metric_${metricId}`];
          });
          return point;
        }),
        campaigns: activeCampaigns,
        userQuestion: customQuestion,
        requestType: 'custom_question' // To differentiate from range analysis
      };

      console.log("Data for custom question to AI:", dataForCustomAI);

      // API endpoint might be the same, but backend will handle based on requestType or separate endpoint
      const response = await api.post('/gemini/ask', dataForCustomAI, { timeout: 600000 }); // Corrected API path
      
      // Basic formatter, assuming backend might send simple HTML or markdown-like lists
      const formatAIResponseForDisplay = (text) => {
        if (!text) return "<p>No response from AI.</p>";
        // Convert common markdown-like lists to HTML lists if backend isn't doing it fully
        let htmlText = text;
        // Replace markdown bold/italic if not already HTML
        htmlText = htmlText.replace(/\*\*\s*(.*?)\s*\*\*/g, '<strong>$1</strong>');
        htmlText = htmlText.replace(/\*\s*(.*?)\s*\*/g, '<em>$1</em>');

        // Convert newlines to <br> for paragraph breaks if not already in <p>
        if (!htmlText.match(/<p>|<br>/i)) {
            htmlText = htmlText.replace(/\n/g, '<br />');
        }
        // Ensure list-like lines are handled (simple conversion to <br> if not already in <li>)
        htmlText = htmlText.replace(/^-{1,}\s*/gm, '<br />&bull; '); // Hyphens/asterisks to bullets
        htmlText = htmlText.replace(/^\*\s+/gm, '<br />&bull; '); // Asterisks to bullets
        htmlText = htmlText.replace(/^\d+\.\s+/gm, (match) => `<br />  ${match}`);
        return htmlText;
      };

      Modal.success({
        title: "Ask Ai Answer",
        content: (
          <div style={{ maxHeight: '60vh', overflowY: 'auto', padding: '10px', border: '1px solid #f0f0f0', borderRadius: '4px' }}>
            <div dangerouslySetInnerHTML={{ __html: formatAIResponseForDisplay(response.data.insight) }} />
          </div>
        ),
        width: 700,
        maskClosable: true,
      });
      setCustomQuestion(""); // Clear question input
    } catch (error) {
      console.error("Error asking custom question to AI:", error);
      Modal.error({
        title: "AI Error",
        content: "Could not get an answer from Ask Ai. Details: " + (error.response?.data?.error || error.message || "Unknown error"),
      });
    } finally {
      setIsAskingCustomQuestion(false);
    }
  };

  // Custom tooltip içeriği - tüm metrikleri gösterir
  const CustomTooltip = ({ active, payload, label }) => {
    if (active && payload && payload.length) {
      return (
        <div style={{ 
          backgroundColor: 'rgba(255, 255, 255, 0.95)', 
          border: '1px solid #ccc', 
          borderRadius: '4px', 
          padding: '10px',
          boxShadow: '0 2px 8px rgba(0,0,0,0.1)'
        }}>
          <p style={{ margin: '0 0 5px 0', fontWeight: 'bold' }}>{label}</p>
          {selectedMetrics.map((metricId, index) => {
            const numericMetricId = typeof metricId === 'string' ? parseInt(metricId, 10) : metricId;
            const foundMetric = availableMetrics.find(m => m.id === numericMetricId);
            const displayName = foundMetric ? (foundMetric.description || foundMetric.name) : `Metrik ${metricId}`;
            
            const dataPoint = payload[0]?.payload;
            if (!dataPoint) return null;
            
            let value, formattedValue;
            if (chartMode === 'compare') {
              value = dataPoint[`normalized_${metricId}`];
              const originalValue = dataPoint[`normalized_${metricId}_original`];
              const formattedOriginal = formatMetricValue(
                originalValue, 
                foundMetric?.dataType || 'number', 
                brandCurrency
              );
              const dailyChange = dataPoint[`normalized_${metricId}_change`];
              const changeText = dailyChange !== undefined ? 
                ` (Değişim: ${dailyChange.toFixed(2)}%)` : '';
              
              formattedValue = `Değer: ${formattedOriginal}${changeText}`;
            } else {
              value = dataPoint[`metric_${metricId}`];
              formattedValue = formatMetricValue(
                value, 
                foundMetric?.dataType || 'number', 
                brandCurrency
              );
            }
            
            if (value === null || value === undefined) return null;
            
            return (
              <div key={metricId} style={{ 
                margin: '2px 0',
                display: 'flex',
                alignItems: 'center'
              }}>
                <div style={{
                  width: '8px',
                  height: '8px',
                  backgroundColor: getLineColor(index),
                  marginRight: '8px',
                  borderRadius: '50%'
                }}></div>
                <span style={{ fontSize: '12px' }}>
                  <strong>{displayName}:</strong> {formattedValue}
                </span>
              </div>
            );
          })}
        </div>
      );
    }
    return null;
  };

  // AI sekmesi açıldığında otomatik metrik seçimi
  useEffect(() => {
    if (activeTab === 'askAI' && availableMetrics.length > 0) {
      // AI sekmesinde tüm metrikler seçili olsun
      const allMetricIds = availableMetrics.map(metric => metric.id);
      setSelectedMetrics(allMetricIds);
    } else if (activeTab === 'chart' && availableMetrics.length > 0) {
      // Normal grafik sekmesinde sadece revenue metriği seçili olsun
      const revenueMetric = availableMetrics.find(metric => metric.id === 1);
      if (revenueMetric) {
        setSelectedMetrics([1]);
      }
    }
  }, [activeTab, availableMetrics]);

  // AI sekmesi açıldığında tüm available metrikleri seç
  const handleTabChange = (key) => {
    setActiveTab(key);
    if (key === 'askAI') {
      // AI sekmesine geçildiğinde chart tab'indeki tarih aralığını koru
      setAiDateRange(dateRange);
      
      if (availableMetrics.length > 0) {
        // AI sekmesinde tüm metrikler seçili
        const allMetricIds = availableMetrics.map(metric => metric.id);
        setSelectedMetrics(allMetricIds);
      }
    } else if (key === 'chart') {
      // Chart sekmesine geçildiğinde AI tab'indeki tarih aralığını koru
      setDateRange(aiDateRange);
      
      if (availableMetrics.length > 0) {
        // Normal grafik sekmesinde sadece revenue
        const revenueMetric = availableMetrics.find(metric => metric.id === 1);
        if (revenueMetric) {
          setSelectedMetrics([1]);
        }
      }
    }
  };

  // AI için tarih aralığı değişikliği
  const handleAiDateRangeChange = (dates) => {
    if (dates && dates.length === 2) {
      setAiDateRange(dates);
    }
  };

  // Mobil kontrol
  const isMobile = window.innerWidth < 768;

  return (
    <>
      {/* Mobil için özel CSS stilleri */}
      {isMobile && (
        <style>
          {`
            .mobile-date-picker .ant-picker-dropdown {
              position: fixed !important;
              top: 50% !important;
              left: 50% !important;
              transform: translate(-50%, -50%) !important;
              max-width: 95vw !important;
              z-index: 1050 !important;
            }
            
            .mobile-date-picker .ant-picker-panel-container {
              max-width: 95vw !important;
            }
            
            .mobile-date-picker .ant-picker-panels {
              flex-direction: column !important;
            }
            
            .mobile-date-picker .ant-picker-panel {
              width: 100% !important;
              max-width: 300px !important;
            }
            
            .mobile-date-picker .ant-picker-header {
              font-size: 14px !important;
            }
            
            .mobile-date-picker .ant-picker-cell-inner {
              min-height: 28px !important;
              line-height: 28px !important;
            }
            
            .mobile-date-picker .ant-picker-footer {
              text-align: center !important;
              padding: 8px 12px !important;
            }
            
            .mobile-date-picker .ant-btn {
              font-size: 12px !important;
              height: 28px !important;
              line-height: 26px !important;
              padding: 0 8px !important;
            }
          `}
        </style>
      )}
      
      <Modal
        title={`${brandName} - Metric Chart`}
        open={visible}
        onCancel={onCancel}
        width={isMobile ? '95vw' : 1200}
        style={isMobile ? { top: 20 } : {}}
        footer={null}
        styles={{ body: isMobile ? { padding: '12px' } : {} }}
      >
      <Tabs defaultActiveKey="chart" activeKey={activeTab} onChange={handleTabChange}>
        <Tabs.TabPane tab="Metric Chart & Analysis" key="chart">
          <div style={{ marginBottom: 16 }}>
            <Space direction="vertical" style={{ width: '100%' }}>
              {/* Mobil için dikey düzen, desktop için yatay */}
              <Space direction={isMobile ? 'vertical' : 'horizontal'} style={{ width: '100%' }}>
                <Radio.Group 
                  value={dataType} 
                  onChange={handleDataTypeChange}
                  disabled={loading}
                  style={{ marginRight: isMobile ? 0 : 16, marginBottom: isMobile ? 8 : 0 }}
                  size={isMobile ? 'small' : 'middle'}
                >
                  <Radio.Button value="daily">Daily</Radio.Button>
                  <Radio.Button value="monthly">Monthly</Radio.Button>
                </Radio.Group>
                
                {dataType === 'daily' ? (
                  <RangePicker 
                    value={dateRange}
                    onChange={handleDateRangeChange}
                    disabled={loading}
                    picker="date"
                    format="DD.MM.YYYY"
                    allowClear={false}
                    size={isMobile ? 'small' : 'middle'}
                    style={{ 
                      width: isMobile ? '100%' : 'auto',
                      minWidth: isMobile ? '280px' : 'auto'
                    }}
                    placeholder={['Başlangıç', 'Bitiş']}
                    dropdownClassName={isMobile ? 'mobile-date-picker' : ''}
                  />
                ) : (
                  <RangePicker 
                    value={dateRange}
                    onChange={handleDateRangeChange}
                    disabled={loading}
                    picker="month"
                    format="MM.YYYY"
                    allowClear={false}
                    size={isMobile ? 'small' : 'middle'}
                    style={{ 
                      width: isMobile ? '100%' : 'auto',
                      minWidth: isMobile ? '280px' : 'auto'
                    }}
                    placeholder={['Başlangıç Ay', 'Bitiş Ay']}
                    dropdownClassName={isMobile ? 'mobile-date-picker' : ''}
                  />
                )}
              </Space>
              
              <Select
                mode="multiple"
                style={{ width: '100%' }}
                placeholder="Select metrics"
                value={selectedMetrics}
                onChange={handleMetricChange}
                disabled={loading || availableMetrics.length === 0}
                maxTagCount={3}
                showSearch
                filterOption={metricFilterOption}
              >
                {availableMetrics.map(metric => (
                  <Option key={metric.id} value={metric.id}>
                    {metric.description || metric.name} 
                    <Tooltip title={
                      metric.internalNotes || 
                      metric.description || 
                      (metric.isAutoCalculated ? "Otomatik Hesaplanan" : "Manuel Metrik")
                    }>
                      <InfoCircleOutlined style={{ marginLeft: 8, color: '#faad14' }} />
                    </Tooltip>
                  </Option>
                ))}
              </Select>
              
              {selectedMetrics.length >= 2 && (
                <Radio.Group 
                  value={chartMode} 
                  onChange={handleChartModeChange}
                  disabled={loading}
                  className="mt-2"
                  size={isMobile ? 'small' : 'middle'}
                >
                  <Radio.Button value="compare">{isMobile ? 'Karşılaştır' : 'Karşılaştırma Modu'}</Radio.Button>
                  <Radio.Button value="normal">{isMobile ? 'Normalize' : 'Normalizasyon'}</Radio.Button>
                </Radio.Group>
              )}

              {/* Chart type selection */}
              {selectedMetrics.length > 0 && (
                <Radio.Group 
                  value={chartType} 
                  onChange={handleChartTypeChange}
                  disabled={loading}
                  className="mt-2"
                  buttonStyle="solid"
                  size={isMobile ? 'small' : 'middle'}
                >
                  <Radio.Button value="line">{isMobile ? 'Line' : 'Line Chart'}</Radio.Button>
                  <Radio.Button value="bar">{isMobile ? 'Bar' : 'Bar Chart'}</Radio.Button>
                  <Radio.Button value="area">{isMobile ? 'Area' : 'Area Chart'}</Radio.Button>
                </Radio.Group>
              )}
            </Space>
          </div>
          
          {loading && chartData.length === 0 ? (
            <div style={{ height: 400, display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
              <Spin size="large" />
            </div>
          ) : selectedMetrics.length === 0 ? (
            <Empty 
              description="Select metrics to display on the chart" 
              style={{ height: 400, display: 'flex', flexDirection: 'column', justifyContent: 'center' }}
            />
          ) : chartData.length === 0 && !loading ? (
            <Empty 
              description="No data found" 
              style={{ height: 400, display: 'flex', flexDirection: 'column', justifyContent: 'center' }}
            />
          ) : (
            <React.Fragment>
              <ResponsiveContainer width="100%" height={isMobile ? 400 : 600}>
                {chartType === 'line' && (
                  <LineChart 
                    data={chartMode === 'normal' ? chartData : normalizedChartData}
                  >
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis 
                      dataKey="displayDate"
                      label={{ 
                        value: dataType === 'daily' ? 'Days' : 'Months', 
                        position: 'insideBottomRight', 
                        offset: -5 
                      }} 
                    />
                    <YAxis 
                      label={{ 
                        value: chartMode === 'normal' ? 'Value' : 'Normalized Value (0-100)', 
                        angle: -90, 
                        position: 'insideLeft' 
                      }}
                      domain={chartMode === 'compare' ? [0, 100] : ['auto', 'auto']}
                      tick={{ fontSize: window.innerWidth < 768 ? 10 : 12 }}
                      width={window.innerWidth < 768 ? 30 : 60}
                    />
                    <RechartsTooltip 
                      content={<CustomTooltip />}
                      position={{ y: 0 }}
                      cursor={{ strokeDasharray: '3 3' }}
                      wrapperStyle={{ zIndex: 1000 }}
                    />
                    <Legend />
                    {selectedMetrics.map((metricId, index) => {
                      const numericMetricId = typeof metricId === 'string' ? parseInt(metricId, 10) : metricId;
                      const metricName = getMetricName(numericMetricId);
                      
                      return (
                        <Line
                          key={metricId}
                          type="monotone"
                          dataKey={chartMode === 'compare' ? `normalized_${numericMetricId}` : `metric_${numericMetricId}`}
                          name={metricName}
                          stroke={getLineColor(index)}
                          activeDot={{ r: 6 }}
                          dot={{ r: 3 }}
                        />
                      );
                    })}
                  </LineChart>
                )}

                {chartType === 'bar' && (
                  <BarChart 
                    data={chartMode === 'normal' ? chartData : normalizedChartData}
                  >
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis 
                      dataKey="displayDate"
                      label={{ 
                        value: dataType === 'daily' ? 'Days' : 'Months', 
                        position: 'insideBottomRight', 
                        offset: -5 
                      }} 
                    />
                    <YAxis 
                      label={{ 
                        value: chartMode === 'normal' ? 'Value' : 'Normalized Value (0-100)', 
                        angle: -90, 
                        position: 'insideLeft' 
                      }}
                      domain={chartMode === 'compare' ? [0, 100] : ['auto', 'auto']}
                      tick={{ fontSize: window.innerWidth < 768 ? 10 : 12 }}
                      width={window.innerWidth < 768 ? 30 : 60}
                    />
                    <RechartsTooltip 
                      content={<CustomTooltip />}
                      position={{ y: 0 }}
                      cursor={{ strokeDasharray: '3 3' }}
                      wrapperStyle={{ zIndex: 1000 }}
                    />
                    <Legend />
                    {selectedMetrics.map((metricId, index) => (
                      <Bar
                        key={metricId}
                        dataKey={chartMode === 'compare' ? `normalized_${metricId}` : `metric_${metricId}`}
                        name={getMetricName(metricId)}
                        fill={getLineColor(index)}
                        barSize={20}
                      />
                    ))}
                  </BarChart>
                )}

                {chartType === 'area' && (
                  <AreaChart 
                    data={chartMode === 'normal' ? chartData : normalizedChartData}
                  >
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis 
                      dataKey="displayDate"
                      label={{ 
                        value: dataType === 'daily' ? 'Days' : 'Months', 
                        position: 'insideBottomRight', 
                        offset: -5 
                      }} 
                    />
                    <YAxis 
                      label={{ 
                        value: chartMode === 'normal' ? 'Value' : 'Normalized Value (0-100)', 
                        angle: -90, 
                        position: 'insideLeft' 
                      }}
                      domain={chartMode === 'compare' ? [0, 100] : ['auto', 'auto']}
                      tick={{ fontSize: window.innerWidth < 768 ? 10 : 12 }}
                      width={window.innerWidth < 768 ? 30 : 60}
                    />
                    <RechartsTooltip 
                      content={<CustomTooltip />}
                      position={{ y: 0 }}
                      cursor={{ strokeDasharray: '3 3' }}
                      wrapperStyle={{ zIndex: 1000 }}
                    />
                    <Legend />
                    {selectedMetrics.map((metricId, index) => (
                      <Area
                        key={metricId}
                        type="monotone"
                        dataKey={chartMode === 'compare' ? `normalized_${metricId}` : `metric_${metricId}`}
                        name={getMetricName(metricId)}
                        fill={getLineColor(index)}
                        stroke={getLineColor(index)}
                        fillOpacity={0.3}
                        activeDot={{ r: 6 }}
                        dot={{ r: 3 }}
                      />
                    ))}
                  </AreaChart>
                )}
              </ResponsiveContainer>
            </React.Fragment>
          )}
        </Tabs.TabPane>
        <Tabs.TabPane tab={<span>✨ Ask Ai</span>} key="askAI">
          <Space direction="vertical" style={{width: "100%"}}>
            <h4>✨ Ask a custom question about the current data:</h4>
            
            {/* Ai için tarih aralığı seçici */}
            <div style={{ marginBottom: 16 }}>
              <Space direction={isMobile ? 'vertical' : 'horizontal'} style={{ width: '100%' }}>
                <span style={{ fontWeight: '500' }}>Tarih Aralığı:</span>
                {dataType === 'daily' ? (
                  <RangePicker 
                    value={aiDateRange}
                    onChange={handleAiDateRangeChange}
                    picker="date"
                    format="DD.MM.YYYY"
                    allowClear={false}
                    size={isMobile ? 'small' : 'middle'}
                    style={{ 
                      width: isMobile ? '100%' : 'auto',
                      minWidth: isMobile ? '280px' : 'auto',
                      marginRight: isMobile ? 0 : 16
                    }}
                    placeholder={['Başlangıç', 'Bitiş']}
                    dropdownClassName={isMobile ? 'mobile-date-picker' : ''}
                  />
                ) : (
                  <RangePicker 
                    value={aiDateRange}
                    onChange={handleAiDateRangeChange}
                    picker="month"
                    format="MM.YYYY"
                    allowClear={false}
                    size={isMobile ? 'small' : 'middle'}
                    style={{ 
                      width: isMobile ? '100%' : 'auto',
                      minWidth: isMobile ? '280px' : 'auto',
                      marginRight: isMobile ? 0 : 16
                    }}
                    placeholder={['Başlangıç Ay', 'Bitiş Ay']}
                    dropdownClassName={isMobile ? 'mobile-date-picker' : ''}
                  />
                )}
              </Space>
            </div>
            
            <Input.TextArea 
              rows={4} 
              placeholder={`e.g., "What are the reasons for the drop in ${selectedMetrics.length > 0 ? getMetricName(selectedMetrics[0]) : 'a specific metric'} during the last week?"`}
              value={customQuestion}
              onChange={(e) => setCustomQuestion(e.target.value)}
              disabled={isAskingCustomQuestion || loading}
            />
             <Button 
              onClick={handleAskCustomQuestion} 
              icon={<UserOutlined />} 
              loading={isAskingCustomQuestion}
              disabled={loading || chartData.length === 0 || selectedMetrics.length === 0 || !customQuestion.trim()}
              type="primary"
            >
              Ask Custom Question to Ask Ai
            </Button>
            <Button 
              onClick={handleAskAI}
              icon={<UserOutlined />} 
              loading={loading && !isAskingCustomQuestion}
              disabled={isAskingCustomQuestion || chartData.length === 0 || selectedMetrics.length === 0}
            >
              Get Full Range Analysis from Ask Ai
            </Button>
          </Space>
        </Tabs.TabPane>
      </Tabs>
      
      <div style={{ textAlign: 'right', marginTop: 20, borderTop: '1px solid #f0f0f0', paddingTop: 10 }}>
        <Button key="close" onClick={onCancel} style={{ marginRight: 8 }}>
            Close
        </Button>
        <Button 
            key="download"
            onClick={handleDownloadCSV} 
            icon={<DownloadOutlined />}
            disabled={loading || selectedMetrics.length === 0 || chartData.length === 0}
            type="primary"
          >
            Download CSV
        </Button>
      </div>
    </Modal>
    </>
  );
};

export default MetricChartModal;